import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Play } from 'lucide-react';
import DemoPlayer from '@/components/DemoPlayer';
import { LogoIcon, UnderaiterMark, TAGLINE } from '@/components/brand/Marks';
import { ShareButtons, watchUrl } from '@/components/ShareDemo';
import { PLATFORM_DEMO } from './DemoPage';

const NAVY = '#0D1B2A';
const TEAL = '#00B4D8';
const ORANGE = '#FF9933';

const fmt = (t) => `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, '0')}`;

function ChapterList({ slides, current, onPick }) {
  return (
    <ol className="space-y-1" data-testid="watch-chapters">
      {slides.map((s, i) => (
        <li key={s.id}>
          <button type="button" onClick={() => onPick(s.time)} data-testid={`watch-chapter-${s.id}`}
            className="w-full flex items-start gap-3 px-3 py-2 rounded-md text-left transition-colors duration-150"
            style={{ background: i === current ? 'rgba(0,180,216,0.12)' : 'transparent', color: i === current ? '#fff' : '#B0BEC5' }}>
            <span className="font-mono text-[11px] mt-0.5 w-9 shrink-0" style={{ color: i === current ? TEAL : '#5C6B7A' }}>{fmt(s.time)}</span>
            <span className="text-sm leading-snug">{s.title}</span>
          </button>
        </li>
      ))}
    </ol>
  );
}

export default function WatchPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const startAt = useMemo(() => Math.max(0, Math.min(Number(params.get('t')) || 0, PLATFORM_DEMO.totalDuration - 1)), [params]);
  const controls = useRef(null);
  const [current, setCurrent] = useState(0);
  const { slides } = PLATFORM_DEMO;

  useEffect(() => {
    const prev = document.title;
    document.title = `Watch the underAIter demo — ${TAGLINE}`;
    return () => { document.title = prev; };
  }, []);

  const requestDemo = () => navigate('/login?mode=waitlist');

  return (
    <div className="min-h-screen" style={{ background: NAVY, color: '#E6EDF3', fontFamily: "'IBM Plex Sans', sans-serif" }} data-testid="watch-page">
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(1200px 500px at 15% -10%, rgba(0,180,216,0.18), transparent 60%), radial-gradient(900px 400px at 100% 0%, rgba(255,153,51,0.12), transparent 60%)' }} />

      <header className="relative max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <button type="button" onClick={() => navigate('/')} className="flex items-center gap-3" data-testid="watch-home">
          <LogoIcon size={30} />
          <UnderaiterMark size="text-lg" dark />
        </button>
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => navigate('/login')} className="hidden sm:block text-sm font-medium transition-colors hover:text-white" style={{ color: '#B0BEC5' }} data-testid="watch-signin">Sign in</button>
          <button type="button" onClick={requestDemo} className="px-4 py-2 rounded-full text-sm font-bold transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5" style={{ background: ORANGE, color: '#fff' }} data-testid="watch-request-demo">
            Request a live demo
          </button>
        </div>
      </header>

      <main className="relative max-w-6xl mx-auto px-5 sm:px-8 pb-20">
        <div className="pt-6 pb-6 max-w-3xl">
          <p className="text-[11px] font-semibold tracking-[0.2em] uppercase flex items-center gap-2" style={{ color: TEAL }} data-testid="watch-eyebrow">
            <Play className="w-3 h-3" fill={TEAL} /> Platform demo · {fmt(PLATFORM_DEMO.totalDuration)}
          </p>
          <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.08] tracking-tight text-white" data-testid="watch-title">{TAGLINE}</h1>
          <p className="mt-3 text-base sm:text-lg leading-relaxed" style={{ color: '#B0BEC5' }}>
            Eleven screens in under three minutes — from the broker's first email to the bound policy to the month-end close.
          </p>
        </div>

        <div className="rounded-2xl p-1.5 sm:p-2" style={{ background: 'rgba(255,255,255,0.06)', boxShadow: '0 30px 80px rgba(0,0,0,0.45)' }}>
          <DemoPlayer
            slides={slides}
            totalDuration={PLATFORM_DEMO.totalDuration}
            audioSrc={PLATFORM_DEMO.audioSrc}
            accentColor={TEAL}
            browserTitle="underaiter.com"
            posterImage={PLATFORM_DEMO.poster}
            embedded
            startAt={startAt}
            controlsRef={controls}
            onSlideChange={setCurrent}
          />
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-10">
          <section>
            <h2 className="text-[11px] font-semibold tracking-[0.2em] uppercase mb-3" style={{ color: '#5C6B7A' }}>Chapters</h2>
            <ChapterList slides={slides} current={current} onPick={(t) => controls.current?.seekTo(t)} />
          </section>

          <aside className="space-y-6">
            <div className="rounded-xl p-5" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }} data-testid="watch-share-card">
              <h2 className="text-[11px] font-semibold tracking-[0.2em] uppercase" style={{ color: '#5C6B7A' }}>Share this demo</h2>
              <p className="mt-2 text-sm" style={{ color: '#B0BEC5' }}>Drop the link into an email or a LinkedIn post — it opens straight into the player.</p>
              <p className="mt-3 font-mono text-xs break-all px-3 py-2 rounded-md" style={{ background: 'rgba(0,0,0,0.3)', color: '#E6EDF3' }} data-testid="watch-share-url">{watchUrl()}</p>
              <div className="mt-4"><ShareButtons url={watchUrl()} /></div>
            </div>

            <div className="rounded-xl p-5" style={{ background: 'linear-gradient(135deg, rgba(255,153,51,0.16), rgba(255,153,51,0.04))', border: '1px solid rgba(255,153,51,0.3)' }}>
              <h2 className="text-lg font-semibold text-white">Want to see it on your own book?</h2>
              <p className="mt-1.5 text-sm" style={{ color: '#B0BEC5' }}>A personalised walkthrough with your submissions, your classes and your brokers.</p>
              <button type="button" onClick={requestDemo} className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold text-white transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5" style={{ background: ORANGE }} data-testid="watch-cta">
                Request a live demo <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </aside>
        </div>
      </main>

      <footer className="relative border-t py-6" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex flex-wrap items-center justify-between gap-3 text-xs" style={{ color: '#5C6B7A' }}>
          <span>© Synaion · {TAGLINE}</span>
          <span>underAIter.com · Sydney, NSW Australia · contact@underaiter.com</span>
        </div>
      </footer>
    </div>
  );
}
